import { useState, useEffect, useRef } from "react";
import { Bell, Menu, PlusCircle, Globe, Check } from "lucide-react";
import { Button } from "./ui/button";
import { useLocation, useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import Cookies from "js-cookie";
import apiClient from "../common/apiClient";
import logo from "../assets/probroker-logo.png";

const NOTIF_BASE = "/ijscui/probroker-admin/ceknvuhbd/Notifications/protected";

export function AppHeader({ onMenuToggle }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang, setLang, t } = useLanguage();

  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const userId = Cookies.get("userId");
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const notifRef = useRef(null);

  const loadNotifications = async () => {
    if (!userId) return;
    const res = await apiClient("get", `${NOTIF_BASE}/${userId}`, { limit: 15 });
    if (res.success) {
      setNotifications(res.data?.data?.rows || []);
      setUnreadCount(res.data?.data?.unreadCount || 0);
    }
  };

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNotifClick = async (n) => {
    if (!n.read) {
      await apiClient("post", `${NOTIF_BASE}/${n._id}/read`);
      setNotifications((prev) => prev.map((x) => (x._id === n._id ? { ...x, read: true } : x)));
      setUnreadCount((c) => Math.max(0, c - 1));
    }
    if (n.data?.link) {
      setNotifOpen(false);
      navigate(n.data.link);
    }
  };

  const handleMarkAllRead = async () => {
    if (!userId) return;
    await apiClient("post", `${NOTIF_BASE}/${userId}/read-all`);
    setNotifications((prev) => prev.map((x) => ({ ...x, read: true })));
    setUnreadCount(0);
  };

  useEffect(() => {
    const controlNavbar = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // scrolling down
        setIsVisible(false);
      } else {
        // scrolling up
        setIsVisible(true);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", controlNavbar);
    return () => {
      window.removeEventListener("scroll", controlNavbar);
    };
  }, [lastScrollY]);

  const isResidentialRental = location.pathname.startsWith("/residential-rental");
  const isFilter = location.pathname === "/filter";
  const isPropertyListed = location.pathname === "/property-listed";

  return (
    <header className={`h-16 bg-white border-b border-border flex items-center justify-between px-4 md:px-6 shadow-card sticky top-0 z-[100] transition-transform duration-300 ${isVisible ? "translate-y-0" : "-translate-y-full"
      }`}>
      <div
        className={`flex items-center gap-1.5 cursor-pointer ${isResidentialRental || isFilter || isPropertyListed ? "" : "md:invisible md:pointer-events-none"
          }`}
        onClick={() => navigate("/dashboard")}
      >
        <img src={logo} alt="Probroker" className="h-6 md:h-8 w-auto" />
      </div>
          
      <div className="flex items-center gap-4">
        <a
          href="https://www.youtube.com/watch?v=Tp8yKAQWt0M"
          target="_blank"
          rel="noopener noreferrer"
          className="hidden md:block text-[#503691] hover:text-[#3d296d] font-medium text-xs md:text-sm hover:underline transition-all"
        >
          Tutorials
        </a>

        <Button
          size="sm"
          className="!bg-[#efe8fd] hover:!bg-[#e4dcf7] !text-[#503691] border border-[#503691] rounded-full flex items-center gap-1.5 text-xs font-bold shadow-sm px-4 md:px-3"
          onClick={() => navigate("/property-listed")}
        >
          <PlusCircle size={14} />
          <span className="hidden md:inline">{t("Add Property", "પ્રોપર્ટી ઉમેરો")}</span>
        </Button>

        <div className="relative hidden md:block" ref={notifRef}>
          <button
            onClick={() => setNotifOpen((o) => !o)}
            className="relative p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <Bell size={20} className="text-muted-foreground" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-destructive text-white text-[10px] font-bold flex items-center justify-center">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>

          {notifOpen && (
            <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white border border-border rounded-xl shadow-xl z-[110]">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <span className="font-semibold text-sm">{t("Notifications", "સૂચનાઓ")}</span>
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="flex items-center gap-1 text-xs text-primary font-medium hover:underline"
                  >
                    <Check size={12} />
                    {t("Mark all read", "બધું વાંચેલ ચિહ્નિત કરો")}
                  </button>
                )}
              </div>
              {notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  {t("No notifications yet", "હજુ સુધી કોઈ સૂચના નથી")}
                </div>
              ) : (
                notifications.map((n) => (
                  <button
                    key={n._id}
                    onClick={() => handleNotifClick(n)}
                    className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-muted transition-colors ${
                      n.read ? "" : "bg-primary-light"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {!n.read && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />}
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{n.title}</p>
                        {n.body && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>}
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {n.createdAt ? new Date(n.createdAt).toLocaleString() : ""}
                        </p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Language Toggle */}
        <button
          onClick={() => setLang(lang === "en" ? "gu" : "en")}
          className="flex items-center gap-1.5 px-2 md:px-3 py-1.5 rounded-full border border-border hover:bg-muted transition-colors text-xs font-semibold shrink-0"
          title={lang === "en" ? "Switch to Gujarati" : "Switch to English"}
        >
          <Globe size={14} className="text-muted-foreground" />
          {lang === "en" ? "ગુજ" : "EN"}
        </button>

        {/* Mobile Menu Toggle */}
        <button onClick={onMenuToggle} className="p-2 rounded-lg hover:bg-muted md:hidden">
          <Menu size={22} />
        </button>
      </div>
    </header>
  );
}